import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector, shallowEqual } from 'react-redux'
import Dashboard from './dashboard'
import Survey from './survey'
import Images from './images'
import PrivateRoute from '../../routes/PrivateRoute'
import ModalContainer from '../../components/ModalContainer'
import Errors from './errors'
import Case from './case'
import Scores from './scores'
import Terms from './terms'
import NewTerms from './newTerms'
import Snackbar from '../../components/Snackbar'
import { Route, Switch } from 'react-router-dom'
import ExternalRoute from '../../routes/ExternalRoute'
import AutoLogoutAlertModal from '../../components/AutoLogoutAlertModal'
import { pageLoadHandler, refreshCurrentCase, getIsCircleTermsAccepted, getIsAuthorizedUser } from '../../redux/actions'
import { caseIdSelector, circleIdSelector, languageSelector, userSelector } from '../../redux/selectors'
import SurveyRedirect from '../../components/SurveyRedirect'
import { ErrorBoundary } from '../../components/ErrorBoundary'
import * as cookies from '../../utilties/cookies'

import { compose } from 'recompose'
import withStyles from '@material-ui/core/styles/withStyles'

const styles = (theme) => ({

	// Set css/scss variables colors, in the same palette as in theme.js
	'@global': {
		':root': {
			'--palette-light': theme.palette.light,
			'--palette-dark': theme.palette.dark,
			'--palette-lightCell': theme.palette.lightCell,
			'--palette-darkCell': theme.palette.darkCell,
			'--palette-blue': theme.palette.blue,
			'--palette-primary-light': theme.palette.primary.light,
			'--palette-primary-A100': theme.palette.primary.A100,
			'--palette-primary-A200': theme.palette.primary.A200,
			'--palette-primary-A300': theme.palette.primary.A300,
			'--palette-primary-main': theme.palette.primary.main,
			'--palette-primary-A500': theme.palette.primary.A500,
			'--palette-primary-A600': theme.palette.primary.A600,
			'--palette-primary-link': theme.palette.primary.link,
			'--palette-primary-A800': theme.palette.primary.A800,
			'--palette-primary-dark': theme.palette.primary.dark,
			'--palette-secondary-light': theme.palette.secondary.light,
			'--palette-secondary-A100': theme.palette.secondary.A100,
			'--palette-secondary-A200': theme.palette.secondary.A200,
			'--palette-secondary-A300': theme.palette.secondary.A300,
			'--palette-secondary-main': theme.palette.secondary.main,
			'--palette-secondary-contrast': theme.palette.secondary.contrast,
			'--palette-secondary-input': theme.palette.secondary.input,
			'--palette-secondary-inputHover': theme.palette.secondary.inputHover,
			'--palette-secondary-additional': theme.palette.secondary.additional,
			'--palette-secondary-A600': theme.palette.secondary.A600,
			'--palette-secondary-A700': theme.palette.secondary.A700,
			'--palette-secondary-A800': theme.palette.secondary.A800,
			'--palette-secondary-dark': theme.palette.secondary.dark,
			'--palette-text-primary': theme.palette.text.primary,
			'--palette-text-secondary': theme.palette.text.secondary,
			'--palette-text-disabled': theme.palette.text.disabled,
			'--palette-text-hint': theme.palette.text.hint,
			'--palette-background-default': theme.palette.background.default,
			'--palette-action-disabledBackground': theme.palette.action.disabledBackground,
			'--palette-action-disabled': theme.palette.action.disabled,
			'--palette-success-main': theme.palette.success.main,
			'--palette-error-main': theme.palette.error.main,
			'--palette-error-light': theme.palette.error.light,
			'--palette-warning-main': theme.palette.warning.main,
			'--palette-info-main': theme.palette.info.main,

		}
	}
})

const Root = () => {
	const dispatch = useDispatch()
 	const caseId = useSelector(caseIdSelector, shallowEqual)
	const [patient] = useState(cookies.get('user'))
	const user = useSelector(userSelector, shallowEqual)
	 

    useEffect(
        () => {
            dispatch(pageLoadHandler());
		},
        []
    )

	useEffect(
        () => {
            dispatch(pageLoadHandler());

            const f = () => dispatch(function () {
                if (!!user) {
                    dispatch(
						getIsAuthorizedUser(user.id)
					)
                }
            });

            f();

            let timer = setInterval(f, 5000)

            return () => {
                clearInterval(timer)
            }
		},
        [user]
    )

    useEffect(
        () => {
			dispatch(refreshCurrentCase());
        },
        [caseId]
    )

	useEffect(
        () => {
			if (!!patient?.id && !!patient?.termsAccepted) {
				dispatch(getIsCircleTermsAccepted(patient.id))
			}
        },
        [patient]
    )

	return (
        <main role="main" className="patient-portal">
			<ErrorBoundary>
            	<Switch>
            	    <ExternalRoute exact={true} path='/clinician' />
					<PrivateRoute
						exact={true}
						path='/survey/:caseId/:protocolId/:instanceId/:surveyId'
						component={Survey}
						title='Benchmarc™ | Survey'
						gaPageTitle={'Survey'}
					/>
					<PrivateRoute
						exact={true}
						path="/error"
						component={Errors}
						title='Benchmarc™ | Error'
						gaPageTitle={'Error'}
					/>
					<PrivateRoute
						exact={true}
						path='/survey/:patientId/:caseId'
						component={SurveyRedirect}
						title='Benchmarc™ | Survey'
						gaPageTitle={'Survey'}
					/>
					<PrivateRoute
						exact={true}
						path='/survey/:patientId'
						component={SurveyRedirect}
						title='Benchmarc™ | Survey'
						gaPageTitle={'Survey'}
					/>
					<PrivateRoute
						exact={true}
						allowExternalAuth={true}
						path="/case"
						component={Case}
						title='Benchmarc™ | Case'
						gaPageTitle={'Case'}
					/>
					<PrivateRoute
						exact={true}
						allowExternalAuth={true}
						path='/albums'
						component={Images}
						title='Benchmarc™ | Albums'
						gaPageTitle={'Albums'}
					/>
					<PrivateRoute
						exact={true}
						allowExternalAuth={true}
						path='/scores'
						component={Scores}
						title='Benchmarc™ | Scores'
						gaPageTitle={'Scores'}
					/>
					<PrivateRoute
						exact={true}
						path='/newTerms'
						component={NewTerms}
						title='Benchmarc™ | Consent'
						gaPageTitle={'Consent'}
					/>
					<PrivateRoute
						exact={true}
						path='/terms/:termId'
						component={Terms}
						title='Benchmarc™ | Consent'
						gaPageTitle={'Consent'}
					/>
					<PrivateRoute
						path='/'
						component={Dashboard}
						title='Benchmarc™ | Home'
						gaPageTitle={'Home'}
					/>
            	</Switch>
            </ErrorBoundary>
			<AutoLogoutAlertModal />
			<ModalContainer />
			<Snackbar />
		</main>
	)
}

const enhance = compose(withStyles(styles))

export default enhance(Root)